// Shell entry point: seed the sim, wire canvas + input + fixed-step loop + renderer.

import { createInitialState, step, type GameState } from './core';
import { computeViewport, sizeCanvas, type Viewport } from './shell/canvas';
import { createInput } from './shell/input';
import { startGameLoop } from './shell/loop';
import { createAudio } from './shell/audio';
import { render } from './render/renderer';

const canvas = document.getElementById('game');
if (!(canvas instanceof HTMLCanvasElement)) {
  throw new Error('#game canvas not found');
}
const ctx = canvas.getContext('2d');
if (!ctx) {
  throw new Error('2D canvas context unavailable');
}

// Seed: ?seed=N for reproducible runs (used by E2E), otherwise time-based.
const seedParam = new URLSearchParams(location.search).get('seed');
const seed = seedParam !== null ? Number(seedParam) >>> 0 : Date.now() >>> 0;

let state: GameState = createInitialState(seed);
let viewport: Viewport = computeViewport(window.innerWidth, window.innerHeight);
sizeCanvas(canvas, viewport);

const input = createInput(canvas, () => viewport);
const audio = createAudio();

const onResize = (): void => {
  viewport = computeViewport(window.innerWidth, window.innerHeight);
  sizeCanvas(canvas, viewport);
};
window.addEventListener('resize', onResize);

// AudioContext must be unlocked from a real user gesture; 'M' toggles mute.
const unlockAudio = (): void => audio.resume();
window.addEventListener('mousedown', unlockAudio);
window.addEventListener('keydown', (e: KeyboardEvent): void => {
  audio.resume();
  if (e.key === 'm' || e.key === 'M') audio.toggleMuted();
});

const livingCities = (s: GameState): number =>
  s.cities.filter((c) => c.alive).length;
const playerBlasts = (s: GameState): number =>
  s.explosions.filter((e) => e.source === 'player').length;

startGameLoop(
  () => {
    const prev = state;
    state = step(state, input.read());
    // Derive SFX from the state delta (keeps the core pure). Blast fires only on
    // player detonations/chains, not on every enemy ground impact.
    if (state.playerMissiles.length > prev.playerMissiles.length) audio.launch();
    if (playerBlasts(state) > playerBlasts(prev)) audio.blast();
    if (livingCities(state) < livingCities(prev)) audio.cityLost();
    if (state.phase.kind === 'gameOver' && prev.phase.kind !== 'gameOver') {
      audio.gameOver();
    }
  },
  () => {
    render(ctx, state, viewport, input.pointer());
  },
);

// Debug hook so E2E can read deterministic state without touching the core
// boundary. Exposed in dev, or for any seeded run (E2E always passes ?seed=).
if (import.meta.env.DEV || seedParam !== null) {
  (window as unknown as { __game: unknown }).__game = {
    getState: (): GameState => state,
    getPhase: (): string => state.phase.kind,
    getScore: (): number => state.score,
  };
}
